import * as React from 'react';

import classnames from 'classnames';
import moment from 'moment';
import type Moment from 'moment';

import {LAST_365_DAYS} from 'nutshell-core/constants';
import {getComparisonDatesFromRange} from 'nutshell-core/get-comparison-dates-from-range';
import {getDateRangeFromDateFilter} from 'nutshell-core/get-date-range-from-date-filter';
import type {ComparisonRangeType} from 'nutshell-core/types';
import {prettifyDateRange} from 'nutshell-core/utils';

import {Button} from '../button/button';
import type {ButtonVariant} from '../button/types';
import {ChevronDownIcon} from '../icon';

import {DateRangePickerForm} from './date-range-picker-form';

import styles from './date-range-picker.module.css';

type DateRangePickerProps = {
    filter: string | null | undefined;
    comparisonType?: ComparisonRangeType;
    onChange: (arg1: string, comparisonType?: ComparisonRangeType) => any;
    dateRangeOptions: Array<{
        id: string;
        value: string;
    }>;
    // Is this date-range-picker being used to mimic a filter capsule?
    // If so, let's match styles and remove comparison functionality
    isFilterCapsule?: boolean;
    // @ts-expect-error
    minDate?: Moment;
    // @ts-expect-error
    maxDate?: Moment;
    variant?: ButtonVariant;
    hideCompareTo?: boolean;
};

type DateRangePickerState = {
    isOpen: boolean;
};

export class DateRangePicker extends React.Component<DateRangePickerProps, DateRangePickerState> {
    static defaultProps = {
        variant: 'secondary',
        hideCompareTo: false,
    };

    buttonRef: HTMLElement | null | undefined;
    fiscalYearStart: string;
    workWeekStart: string;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isOpen: false,
        };

        this.fiscalYearStart =
            typeof NutshellFrontendSettings !== 'undefined'
                ? NutshellFrontendSettings.fiscal_year
                : '1-1';

        this.workWeekStart =
            typeof NutshellFrontendSettings !== 'undefined'
                ? NutshellFrontendSettings.work_week_start
                : '7';
    }

    render() {
        const filter =
            this.props.filter || getDefaultFilter(this.props.minDate, this.props.maxDate);

        const buttonStyles = classnames('ui-btn--big', {
            [styles.button]: true,
            [styles['button-comparing']]: this.props.comparisonType,
            'ui-btn--active': this.state.isOpen,
        });

        const dateRange = getDateRangeFromDateFilter(
            filter,
            moment(),
            this.fiscalYearStart,
            this.workWeekStart
        );

        const dateDisplay = this.props.filter
            ? prettifyDateRange(dateRange.startDate, dateRange.endDate)
            : 'All time';

        // Comparison mode - we'll generate these dates only if there's a
        // comparisonType present
        let comparisonDateDisplay;
        if (this.props.comparisonType) {
            const comparisonRange = getComparisonDatesFromRange(
                filter,
                this.props.comparisonType,
                this.fiscalYearStart,
                this.workWeekStart
            );

            comparisonDateDisplay = prettifyDateRange(
                comparisonRange.startDate,
                comparisonRange.endDate
            );
        }

        return (
            <div>
                <Button
                    className={buttonStyles}
                    onClick={this.handleClick}
                    // @ts-expect-error
                    getButtonRef={(node) => {
                        this.buttonRef = node;
                    }}
                    variant={this.props.variant}
                >
                    <div
                        className={
                            this.props.isFilterCapsule
                                ? styles['date-container--filter-capsule']
                                : styles['date-container']
                        }
                    >
                        <span>{dateDisplay}</span>
                        {this.props.comparisonType ? (
                            <span className={styles['date-comparison']}>
                                Compared to: {comparisonDateDisplay}
                            </span>
                        ) : undefined}
                    </div>
                    <ChevronDownIcon
                        className={
                            this.props.isFilterCapsule
                                ? styles['chevron-down-icon--filter-capsule']
                                : styles['chevron-down-icon']
                        }
                    />
                </Button>
                {this.state.isOpen && this.buttonRef ? (
                    <DateRangePickerForm
                        anchor={this.buttonRef}
                        filter={filter}
                        comparisonType={this.props.comparisonType}
                        dateRangeOptions={this.props.dateRangeOptions}
                        onClose={this.handleClose}
                        onChange={this.handleChange}
                        hasComparison={Boolean(
                            !this.props.isFilterCapsule && !this.props.hideCompareTo
                        )}
                        minDate={this.props.minDate}
                        maxDate={this.props.maxDate}
                        isFilterCapsule={this.props.isFilterCapsule}
                    />
                ) : undefined}
            </div>
        );
    }

    handleClose = () => {
        this.setState({isOpen: false});
    };

    handleClick = () => {
        this.setState((prevState) => ({isOpen: !prevState.isOpen}));
    };

    handleChange = (filterString: string, comparisonType?: ComparisonRangeType) => {
        this.setState({isOpen: false});
        this.props.onChange(filterString, comparisonType);
    };
}

// When a filter isn't passed, we need to display _something_ so we'll try and
// use the minDate and maxDate to display a range, otherwise we'll just default
// to the last 365 days as a sane backup
// @ts-expect-error
function getDefaultFilter(minDate?: Moment | null, maxDate?: Moment | null) {
    if (minDate && maxDate) {
        return `${minDate.unix()} TO ${maxDate.unix()}`;
    }

    // eslint-disable-next-line no-console
    console.warn(
        'filter not passed, and no minDate/maxDate to go on, defaulting to the last 365 days'
    );

    return LAST_365_DAYS.id;
}
